import {
  ButtonProps,
  getActiveBackgroundColor,
  getBackgroundColor,
  getBorder,
  getFillColor,
  getHoverBackgroundColor,
  getHoveredBorder,
  getHoverFontColor,
} from "@src/components/elements/ButtonVariants";
import { HeaderSlotPosition } from "@src/components/modules/Table/tableInterfaces";
import styled from "styled-components/macro";

export interface StyledTableProps {
  hasStickyHeader?: boolean;
  stickyHeaderOffsetTop?: number;
  backgroundColor?: string;
  padding?: boolean;
  borderCells?: boolean;
  className?: string;
  headerSlotPosition?: HeaderSlotPosition;
  hasFilter?: boolean;
  theadWidth?: number;
  headerBackground?: "white" | "gray";
  isRounded?: boolean;
}

export const StyledTableHeader = styled.div<StyledTableProps>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: ${(props) => (props.hasFilter ? "space-between" : "center")};
  color: var(--gray);
  font-size: var(--font-size-xxsmall);
  text-align: left;
  padding: 0.375rem 0.75rem;
  line-height: 1rem;
  font-weight: var(--font-weight-medium);
  border-right: ${(props) =>
    props.borderCells ? "solid 1px var(--border-gray-light)" : null};
  border-bottom: 1px solid var(--border-gray-light);
  background: ${({ headerBackground }) =>
    headerBackground === "white"
      ? "var(--page-background)"
      : "var(--background-gray)"};
  user-select: none;
  cursor: default !important;

  &.align-right {
    align-items: flex-end;
  }
  &.align-center {
    align-items: center;
  }

  .header-title {
    display: flex;
    align-items: ${(props) => (props.hasFilter ? "flex-start" : "center")};
    height: 100%;
    margin: 0 -0.5rem;
    padding: ${(props) => (props.hasFilter ? "0.35rem" : "0.25rem")} 0.5rem;
    border-radius: var(--rounded-corner);
    transition: var(--transition);
    cursor: pointer;

    &:hover {
      background: var(--border-gray-light);
    }

    &:hover:has(button) {
      background: transparent;
    }

    &:has(> .header-title-render:empty) {
      display: none;
    }

    .header-title-render {
      padding-top: ${(props) => (props.hasFilter ? "0.25rem" : "inherit")};
    }
  }

  .row input {
    width: 100%;
  }
`;

type StyledTableRootProps = StyledTableProps & {
  height?: number;
  width?: number;
  maxHeight?: number;
};
export const StyledTableRoot = styled.div<StyledTableRootProps>`
  z-index: 0;
  background-color: ${(props) =>
    props.backgroundColor ? props.backgroundColor : "var(--page-background)"};
  padding: ${(props) => (props.padding ? "0 1.5rem 5rem 1.5rem" : "")};
  max-height: ${(props) => (props.height ? `${props.height}px` : "")};
  max-width: ${(props) => (props.width ? `${props.width}px` : "100%")};
  height: ${(props) => (props.height ? `${props.height}px` : "fit-content")};
  width: ${(props) => (props.width ? `${props.width}px` : "100%")};
  display: flex;
  flex-direction: column;
  animation: slide-down 0.3s ease-out;
  .thead {
    ${(props) => (props.theadWidth ? `width: ${props.theadWidth}px;` : "")}
  }

  header {
    display: flex;
    align-items: center;
    padding: 1rem 2rem;
    gap: 0.5rem;

    .header-left {
      flex-grow: ${(props) =>
        props.headerSlotPosition && props.headerSlotPosition === "left"
          ? 0
          : 1};
      align-items: center;
      justify-content: start;
      display: flex;
      gap: 1rem;
    }

    .header-right {
      flex-grow: ${(props) =>
        props.headerSlotPosition && props.headerSlotPosition === "left"
          ? 1
          : 0};
      display: flex;
      gap: 0.5rem;
      justify-content: end;
      align-items: center;

      .header-slot {
        display: flex;
        flex-grow: ${(props) =>
          props.headerSlotPosition && props.headerSlotPosition === "left"
            ? 1
            : 0};
        align-items: center;
      }
    }
  }

  .filter-menu {
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    color: var(--green);
    font-weight: var(--font-weight-bold);
    display: inline-flex;
    padding: var(--btn-padding-md);
    border-radius: var(--rounded-corner);
    transition: var(--transition);
    svg {
      fill: var(--green);
    }
    &:hover {
      background-color: var(--background-green);
    }
  }

  .table-wrap {
    flex-grow: 1;
    width: calc(100% - 4rem);
    margin: auto;
    display: block;
    overflow-x: auto;
    border: 1px solid var(--border-gray-light);
    box-shadow: var(--drop-shadow-light);
    background-color: var(--white);
    border-radius: var(--rounded-corner-large);

    &.fi-table {
      border-radius: 0;
      border-top: 1px solid var(--border-gray-light);
      border-left: 0;
      border-bottom: 0;
      border-right: 0;
      box-shadow: none;
    }
  }

  .pagination {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding-left: 3rem;
    .pagination-count {
      font-size: var(--font-size-xxsmall);
      font-weight: var(--font-weight-bold);
      color: var(--light-gray);
      padding: 1.5rem 0;
    }
    .pagination-nav {
      display: flex;
      align-items: center;
      padding: 1rem 1rem;
      gap: 0.5rem;
    }
  }
`;

export const StyledDivTable = styled.div<StyledTableProps>`
  box-sizing: border-box;
  min-width: 100%;
  width: 100%;
  height: fit-content;
  min-height: 100%;
  border-collapse: collapse;
  font-family: var(--inter-font);
  @media only screen and (max-width: 40rem) {
    min-width: fit-content !important;
  }

  .fixed-width {
    flex: 0 0 auto !important;
  }

  .pad-left {
    padding-left: 2rem;
  }

  .bg-white {
    background: var(--white);
  }

  .link,
  .colored-link {
    cursor: pointer;
  }
  .colored-link {
    color: var(--green) !important;
  }

  .border-sides {
    border-left: 1px solid var(--border-gray-light);
    border-right: 1px solid var(--border-gray-light);
  }

  .border-left {
    border-left: 1px solid var(--border-gray-light);
  }

  .border-right {
    border-right: 1px solid var(--border-gray-light);
  }

  thead,
  .thead {
    flex: 0 1 auto;
    background-color: ${({ headerBackground }) =>
      headerBackground === "white" ? "var(--white)" : "var(--page-background)"};
    ${(props) =>
      props.hasStickyHeader
        ? `
        position: sticky;
        top: ${props.stickyHeaderOffsetTop || 0}px;
        z-index: 10000;
      `
        : undefined}

    tr,
    .tr {
      font-weight: var(--font-weight-medium);

      th,
      .th {
        .icon {
          width: 1rem;
        }
      }

      .bg-white {
        background: var(--background-gray);
      }

      .in-front {
        z-index: 10001;
      }
    }

    .header-padding {
      padding: 0;
    }
  }

  tbody,
  .tbody {
    flex: 1 1 auto;

    tr,
    .tr {
      align-items: stretch;
      .action-button {
        opacity: 0.4;
      }

      &:hover td,
      &:hover .td,
      &:hover th,
      &:hover .th,
      &:hover .breakdown-table-cell.th {
        background: var(--background-gray);
        .bg-white {
          background: var(--background-gray);
        }
        .action-button {
          opacity: 1;
        }
      }

      &:hover td,
      &:hover .td {
        color: var(--primary-black);
        fill: var(--secondary-black);
      }

      &:hover .color-green {
        color: var(--green);
      }

      th,
      .th,
      td,
      .td {
        fill: var(--border-gray);
        color: var(--secondary-black);
        font-size: var(--font-size-xsmall);
        text-align: left;
        padding: 0.5rem 0.75rem;
        line-height: 1.5rem;
        font-weight: var(--font-weight-regular);
        border-bottom: 1px solid var(--border-gray-light);
        background: var(--white);
      }

      td,
      .td {
        &.align-right {
          text-align: right;
          div {
            justify-content: flex-end;
          }
        }
        &.sub-table {
          background-color: var(--light-gray);
        }
      }

      &.disabled {
        .th,
        .td {
          background: var(--page-background);
          color: var(--light-gray);
        }
        &:hover {
          .td,
          .th {
            background: var(--background-gray);
          }
        }
      }

      .breakdown-table-cell.th {
        background: var(--white);
      }

      .breakdown-table-cell {
        padding: 0.25rem 0.75rem;
        font-size: var(--font-size-xxsmall);
      }

      .bg-gray {
        background-color: var(--page-background);
      }

      .bold {
        font-weight: var(--font-weight-bold);
      }

      .very-bold {
        font-weight: var(--font-weight-bold);
      }

      .color-green {
        color: var(--green);
      }
    }

    .tr {
      display: flex;
    }
  }
`;

export const StyledTable = StyledDivTable.withComponent("table");
export const StyledTHead = styled.thead`
  tr {
    th {
      color: var(--gray);
      font-size: var(--font-size-xxsmall);
      text-align: left;
      padding: 0.375rem 0.75rem;
      line-height: 1rem;
      font-weight: var(--font-weight-medium);
      border-bottom: 1px solid var(--border-gray-light);
      background: var(--background-gray);
      user-select: none;
    }
  }
`;

export const StyledTableTitle = styled.h1`
  font-size: 1.1rem;
`;

export const StyledTableIcons = styled.div`
  display: flex;
  flex-direction: column;
  padding-left: 0.25rem;
  justify-content: center;
  align-items: center;
  gap: 0rem;

  & > * {
    height: 0.625rem;
    align-items: center;
  }

  .hide-icon {
    height: 0;
    opacity: 0;
    transition: var(--transition);
    margin: 0;
  }
`;

export const StyledNameCell = styled.div<{
  justifyContent?: string;
  secondaryLink?: boolean;
  gap?: string;
  flexDirection?: "row" | "column";
  alignItems?: "center" | "flex-start";
  enableCopyToClipboard?: boolean;
  overflow?: "auto" | "hidden";
  width?: string;
}>`
  display: flex;
  flex-direction: ${(props) => props.flexDirection ?? "row"};
  gap: ${(props) => props.gap ?? "1rem"};
  white-space: nowrap;
  align-items: ${(props) => props.alignItems ?? "center"};
  justify-content: ${(props) => props.justifyContent ?? "flex-start"};
  height: 100%;
  max-width: 100%;
  overflow: ${(props) => props.overflow ?? "hidden"};
  width: ${(props) => props.width ?? "auto"};
  &:hover {
    background-position: bottom right;
    background-image: ${(props) =>
      props.enableCopyToClipboard
        ? "url(data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIGhlaWdodD0iMjRweCIgdmlld0JveD0iMCAtOTYwIDk2MCA5NjAiIHdpZHRoPSIyNHB4IiBmaWxsPSIjZThlYWVkIj48cGF0aCBkPSJNMzYwLTI0MHEtMzMgMC01Ni41LTIzLjVUMjgwLTMyMHYtNDgwcTAtMzMgMjMuNS01Ni41VDM2MC04ODBoMzYwcTMzIDAgNTYuNSAyMy41VDgwMC04MDB2NDgwcTAgMzMtMjMuNSA1Ni41VDcyMC0yNDBIMzYwWm0wLTgwaDM2MHYtNDgwSDM2MHY0ODBaTTIwMC04MHEtMzMgMC01Ni41LTIzLjVUMTIwLTE2MHYtNTYwaDgwdjU2MGg0NDB2ODBIMjAwWm0xNjAtMjQwdi00ODAgNDgwWiIvPjwvc3ZnPg==)"
        : "none"};
    background-repeat: no-repeat;
  }

  .copy-icon {
    opacity: 0;
  }

  a,
  span {
    text-overflow: ellipsis;
    overflow: hidden;
  }

  a {
    font-weight: ${(props) =>
      props.secondaryLink
        ? "var(--font-weight-regular)"
        : "var(--font-weight-bold)"};
    cursor: pointer;
    color: ${(props) =>
      props.secondaryLink ? "var(--secondary-black)" : "var(--focus-green)"};
    text-decoration: ${(props) => (props.secondaryLink ? "underline" : "")};
  }

  .icon-container {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .icon {
    height: 1.25rem;
    cursor: pointer;
  }
  p {
    margin: 0;
  }
`;

export const StyledTableIconButton = styled.button<ButtonProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${(props) => getBackgroundColor(props)};
  fill: ${(props) => getFillColor(props)};
  border-radius: 0.25em;
  border: ${(props) => getBorder(props)};
  cursor: ${(props) => (props.disabled ? "default" : "pointer")};
  transition: var(--transition);
  height: 1.75rem;
  width: 1.75rem;

  &:hover {
    background-color: ${(props) => getHoverBackgroundColor(props)};
    color: ${(props) => getHoverFontColor(props)};
    border: ${(props) => getHoveredBorder(props)};
    box-shadow: var(--drop-shadow-light);
  }

  &:active,
  &:focus {
    background-color: ${(props) => getActiveBackgroundColor(props)};
  }

  svg {
    height: 1.25rem;
    width: 1.25rem;
  }
`;
